import { customizer } from '@sas/design-tokenizer';
import { getLogger } from '@sas/nova-core/logging';
import PRODUCT_TOKENS_JSON from '@sas/product-tokens/processed-tokens.json';
import { isBaseThemeName } from './themeOverrides';
import { defaultThemes, ReportThemeParametersMap } from './themeParameters';
const logger = getLogger('reportThemes');

/**
 * Creates a `ThemeProvider` compatible theme from a set of `ThemeParameterOverrides`.
 *
 * NOTE: Prioritizes `baseReportThemeName` to be set to the desired report theme to work
 * properly. Falls back to `baseThemeName`.
 *
 * @param overrides `ThemeParameterOverrides`
 * @returns `ThemeParameters`
 */
export function resolveReportThemeOverrides(overrides) {
  let baseTheme;
  const reportThemeName = overrides.baseReportThemeName ?? overrides.baseThemeName;
  if (isSystemReportTheme(reportThemeName)) {
    baseTheme = getReportThemeParameters(reportThemeName);
  } else {
    // eslint-disable-next-line @sas/i18n/jsx-to-localize
    logger.warn(`Unsupported 'baseReportThemeName' provided ('${reportThemeName}'). Defaulting to 'light'.`);
    baseTheme = getReportThemeParameters('light');
  }
  const productTokens = PRODUCT_TOKENS_JSON;
  const customizedTokens = customizer(productTokens, {
    'color-neutral': overrides.colors?.neutral ?? baseTheme.colors.neutral,
    'color-primary': overrides.colors?.primary ?? baseTheme.colors.primary,
    'color-secondary': overrides.colors?.secondary ?? baseTheme.colors.secondary,
    'color-status-alert': overrides.colors?.alert ?? baseTheme.colors.alert,
    'color-status-warning': overrides.colors?.warning ?? baseTheme.colors.warning,
    'color-status-info': overrides.colors?.information ?? baseTheme.colors.information,
    'color-status-success': overrides.colors?.success ?? baseTheme.colors.success,
    'color-dataviz-graph1': overrides.colors?.colorGraph1 ?? baseTheme.colors.colorGraph1,
    'color-dataviz-graph2': overrides.colors?.colorGraph2 ?? baseTheme.colors.colorGraph2,
    'color-dataviz-graph3': overrides.colors?.colorGraph3 ?? baseTheme.colors.colorGraph3,
    'color-dataviz-graph4': overrides.colors?.colorGraph4 ?? baseTheme.colors.colorGraph4,
    'color-dataviz-graph5': overrides.colors?.colorGraph5 ?? baseTheme.colors.colorGraph5,
    'color-dataviz-graph6': overrides.colors?.colorGraph6 ?? baseTheme.colors.colorGraph6,
    'color-dataviz-graph7': overrides.colors?.colorGraph7 ?? baseTheme.colors.colorGraph7,
    'color-dataviz-graph8': overrides.colors?.colorGraph8 ?? baseTheme.colors.colorGraph8,
    'color-dataviz-kpi1': overrides.colors?.colorKPI1 ?? baseTheme.colors.colorKPI1,
    'color-dataviz-kpi3': overrides.colors?.colorKPI3 ?? baseTheme.colors.colorKPI3,
    'color-dataviz-kpi5': overrides.colors?.colorKPI5 ?? baseTheme.colors.colorKPI5,
    'brand-color-blue': overrides.colors?.primary ?? baseTheme.colors.primary,
    'brand-color-grey': overrides.colors?.neutral ?? baseTheme.colors.neutral,
    'brand-color-cranberry': overrides.colors?.secondary ?? baseTheme.colors.secondary,
    'brand-color-info': overrides.colors?.information ?? baseTheme.colors.information,
    'brand-color-warning': overrides.colors?.warning ?? baseTheme.colors.warning,
    'brand-color-alert': overrides.colors?.alert ?? baseTheme.colors.alert,
    'brand-color-success': overrides.colors?.success ?? baseTheme.colors.success
    // @fontFamilyOverride is handled in react.ts as it needs to be locale aware
  });

  const tokensExperimental = {};
  (customizedTokens.find(t => t.name === overrides.baseThemeName)?.tokens ?? []).forEach(({
    name,
    value
  }) => {
    // font-family needs to be locale aware so the overrides are being done in react.ts
    if (name === 'font-family-primary' || name === 'font-family-secondary') return;
    tokensExperimental[name] = value;

    // Report controls need to have custom font sizes as they cannot rely upon the browser root font size changing
    if (name.startsWith('font-size')) {
      const fontSizePx = parseFloat(value) * 16;
      const scalar = parseFloat(overrides.overrides?.fontSizeBase ?? '100%') / 100;
      const newFontSize = `${Math.round(fontSizePx * scalar * 1000) / 1000}px`;
      tokensExperimental[name] = newFontSize;
      return;
    }
  });
  const enableNovaTokens = overrides.baseReportThemeName === 'light2025' || overrides.baseReportThemeName === 'dark2025' || overrides.baseReportThemeName === 'highcontrast2020';
  return {
    ...baseTheme,
    tokensExperimental,
    tokens: tokensExperimental,
    colors: {
      ...baseTheme.colors,
      ...overrides.colors
    },
    overrides: {
      ...baseTheme.overrides,
      ...overrides.overrides
    },
    enableNovaTokens
  };
}

/**
 * Get the base set of `ThemeParameters` that make up the requested report theme.
 *
 * @param reportThemeName SAS supported report theme id
 * @returns
 */
export function getReportThemeParameters(reportThemeName) {
  if (!isSystemReportTheme(reportThemeName)) {
    return defaultThemes.light;
  }
  const {
    baseThemeName,
    colors
  } = ReportThemeParametersMap[reportThemeName];
  let baseAppThemeName = baseThemeName;
  if (!isBaseThemeName(baseAppThemeName)) {
    // eslint-disable-next-line @sas/i18n/jsx-to-localize
    logger.warn(`Unsupported 'baseThemeName' provided ('${baseAppThemeName}'). Defaulting to 'light'.`);
    baseAppThemeName = 'light';
  }
  const baseTheme = defaultThemes[baseAppThemeName];
  const isTheme2025Enabled = reportThemeName === 'light2025' || reportThemeName === 'dark2025' || reportThemeName === 'highcontrast2020';
  return {
    ...baseTheme,
    colors: {
      ...baseTheme.colors,
      ...colors
    },
    baseReportThemeName: reportThemeName,
    enableNovaTokens: isTheme2025Enabled
  };
}
export function isSystemReportTheme(themeName) {
  return ReportThemeParametersMap.hasOwnProperty(themeName);
}
export function isDarkReportTheme(theme) {
  const isReportTheme = !!theme.baseReportThemeName;
  const isDark = theme.baseThemeName === 'dark' || theme.baseThemeName === 'high-contrast';
  return isReportTheme && isDark;
}
export function getReportThemeColorDerivation(theme) {
  switch (theme.baseReportThemeName) {
    case 'marine':
    case 'midnight':
    case 'opal':
    case 'sail':
    case 'aqua':
    case 'umstead':
    case 'highcontrast2018':
    case 'highcontrast2017':
    case 'light':
    case 'dark':
    case 'snow':
    case 'light2025':
    case 'dark2025':
    case 'highcontrast':
      return '2019';
    case 'midnight2020':
    case 'highcontrast2020':
    default:
      return 'latest';
  }
}
//# sourceMappingURL=reportThemes.js.map